import { useEffect, useRef, useState } from 'react';
import { DataDirection, IPosition2d } from '../../../../core/types/ui/position';
import { Size } from '../../../../core/types/ui/size';
import { UseSheetTheme } from '../../../hooks/useSheetTheme';
import { ReturnTypeofSheetSectionsSelector } from '../../../selectors/sheetUiSelector';
// import { getUnHiddenRowCol } from '../../../utils/viewport/viewportUtils';
import useDraggable from '../../../../core/hooks/useDraggable';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../core/hooks/storeHooks';
import { selectSheet } from '../../../selectors/sheetSelector';
import { findWindowBySheetId } from '../../../../windows/redux/windowsSlice';
import {
  getColPositionFromClientPos,
  getRowPositionFromClientPos,
} from '../../../utils/selection/selection';
import { SheetDefaults } from '../../../constants/sheetDefaults';
import {
  FilterResults,
  GridEntity,
  SelectedLineType,
  Sheet,
  Viewport,
  // Viewport,
} from '../../../types';
import { AppWindow } from '../../../../windows/types';
import { getViewportStartEnd } from '../../../utils/views/virtualViewport';
import _ from 'lodash';
import { adjustNonFrozenViewportWithFreezeAc } from '../../../redux/sagas/views/viewsActions';
import { UseGridOperations } from '../../../hooks/useGridOperations';
import { SheetCommandType } from '../../../types/commands/sheetCommands/sheetCommands';
import { handleSheetOperations } from '../../../redux/sagas/root/rootActions';

export interface MoveableFrozenLineProps
  extends Size,
    IPosition2d,
    ReturnTypeofSheetSectionsSelector,
    Pick<
      UseSheetTheme,
      'stroke' | 'selectionBorder' | 'selectionFill' | 'isRtl'
    > {
  sheetId: string;
  windowWidth: number;
  windowHeight: number;
}

export default ({
  sheetId,
  x: windowX,
  y: windowY,
  isRtl,
  width,
  height,
  freezedCols,
  freezedColsSum,
  freezedRows,
  freezedRowsSum,
  rowHeaderWidth,
  zoom,
  // viewport,
  ...props
}: MoveableFrozenLineProps) => {
  const dispatch = useAppDispatch();
  const sheet = useAppSelector((state) => selectSheet(state, sheetId));
  const window = useAppSelector((state) => findWindowBySheetId(sheetId)(state));

  const [selectedLine, setSelectedLine] = useState<SelectedLineType>('none');
  const selectedLineRef = useRef<SelectedLineType>('none');
  const viewportRef = useRef({
    colViewport: props.colViewport,
    rowViewport: props.rowViewport,
  });

  useEffect(() => {
    viewportRef.current.colViewport = props.colViewport;
    viewportRef.current.rowViewport = props.rowViewport;
  }, [props.colViewport, props.rowViewport]);

  const [linePosX, setLinePosX] = useState(0);
  const [linePosY, setLinePosY] = useState(0);
  // const [clientPos, setClientPos] = useState(0);

  const [expectedIndex, setExpectedIndex] = useState(-1);
  const freezeBarHandleSize = SheetDefaults.freezeBarHandleSize * zoom;

  const leftSum = freezedColsSum + rowHeaderWidth;

  const unFreezedLeft = isRtl ? width - leftSum : leftSum - freezeBarHandleSize;
  const freezedLeft = isRtl ? width - leftSum - freezeBarHandleSize : leftSum;
  const left = freezedCols.frozen ? freezedLeft : unFreezedLeft;

  const unFreezedTop = props.colHeaderHeight - freezeBarHandleSize; // props.groupBarHeight +
  const freezedTop =
    freezedRowsSum +
    // props.groupBarHeight +
    props.colFilterHeight +
    props.colHeaderHeight;
  const top = freezedRows.frozen ? freezedTop : unFreezedTop;

  useEffect(() => {
    if (selectedLine === 'none') {
      setLinePosX(left);
      setLinePosY(top);
    }
  }, [left, top, selectedLine]);

  const onLineDrag = (
    x: number,
    y: number,
    e: MouseEvent,
    dragEnd?: boolean
  ) => {
    if (window && sheet) {
      let newPosition = -1;
      if (window && sheet && selectedLineRef.current === GridEntity.Column) {
        newPosition = getExpectedColIndex(
          window,
          sheet,
          viewportRef.current.colViewport,
          viewportRef.current.rowViewport,
          // props.colViewport,
          // viewport,
          freezedColsSum,
          isRtl,
          e.clientX
        );
        setLinePosX(x);
      } else if (
        window &&
        sheet &&
        selectedLineRef.current === GridEntity.Row
      ) {
        newPosition = getExpectedRowIndex(
          window,
          sheet,
          viewportRef.current.rowViewport,
          // props.rowViewport,
          // viewport,
          freezedRowsSum,
          e.clientY
        );
        setLinePosY(y);
      }

      setExpectedIndex(newPosition);

      if (dragEnd) {
        const entityType: GridEntity = selectedLineRef.current as GridEntity;

        const viewport =
          entityType === GridEntity.Column
            ? props.colViewport
            : props.rowViewport;

        const freeze =
          entityType === GridEntity.Column ? freezedCols : freezedRows;

        const { start: frozenStart } = getViewportStartEnd(
          viewport.frozenIndexes
        );
        const { start: nonFrozenStart } = getViewportStartEnd(
          viewport.nonFrozenIndexes
        );

        const start = frozenStart !== -1 ? frozenStart : nonFrozenStart;

        selectedLineRef.current = 'none';
        setSelectedLine('none');
        // console.log('drag ends', newPosition);

        // const op =
        //   newPosition === -1
        //     ? SheetCommandType.UnfreezeGrid
        //     : SheetCommandType.FreezeGrid;

        const isUnFrozenOp = newPosition === -1;

        if (isUnFrozenOp && !freeze.frozen) return;
        if (!isUnFrozenOp && freeze.end === newPosition) return;
        //   if (op === SheetCommandType.UnfreezeGrid && !freeze.frozen) return;
        // if (op === SheetCommandType.FreezeGrid && freeze.end === newPosition)
        //   return;

        dispatch(
          handleSheetOperations({
            sheetId,
            type: SheetCommandType.FreezeGrid,
            range: { start, end: newPosition },
            entity: entityType,
          })
        );
      
      }
    }
  };

  const { onMouseDown } = useDraggable({
    x: left,
    y: top,
    onDrag: onLineDrag,
  });

  const startMouseDown = (e: React.MouseEvent, lineType: SelectedLineType) => {
    // This Action will align viewport
    dispatch(
      adjustNonFrozenViewportWithFreezeAc({
        sheetId: sheetId,
        windowWidth: props.windowWidth,
        windowHeight: props.windowHeight,
        adjustCols: lineType === GridEntity.Column,
        adjustRows: lineType === GridEntity.Row,
      })
    );
    if (lineType === GridEntity.Column) {
      const { end } = getViewportStartEnd(props.colViewport.frozenIndexes);
      setExpectedIndex(freezedCols.frozen ? end : -1);
    } else if (lineType === GridEntity.Row) {
      const { end } = getViewportStartEnd(props.rowViewport.frozenIndexes);
      setExpectedIndex(freezedRows.frozen ? end : -1);
    }
    setSelectedLine(lineType);
    onMouseDown(e);
    selectedLineRef.current = lineType;
  };

  return {
    expectedIndex,
    selectedLine,
    left,
    top,
    // clientPos,
    linePosX,
    linePosY,
    freezeBarHandleSize,
    startMouseDown,
  };
};

// #region helpers
const getExpectedColIndex = (
  window: AppWindow,
  sheet: Sheet,
  colViewport: Viewport,
  rowViewport: Viewport,
  freezedColsSum: number,
  isRtl: boolean,
  clientX: number
) => {
  const { end } = getViewportStartEnd(colViewport.nonFrozenIndexes);
  // const lastCe = _.last(virtualViewport.nonFrozenIndexes) ?? 0;
  let expectedIndex = getColPositionFromClientPos(
    window.x,
    window.width,
    sheet,
    colViewport,
    rowViewport,
    freezedColsSum,
    isRtl,
    clientX,
    true
  );
  if (expectedIndex === end) {
    const secondToLastElement = _.nth(colViewport.nonFrozenIndexes, -2);
    if (secondToLastElement !== undefined) {
      expectedIndex = secondToLastElement;
    }
  }

  return expectedIndex;
};

const getExpectedRowIndex = (
  window: AppWindow,
  sheet: Sheet,
  rowViewport: Viewport,
  freezedRowsSum: number,
  clientY: number
) => {
  const { end } = getViewportStartEnd(rowViewport.nonFrozenIndexes);
  // const lastRe = _.last(virtualViewport.nonFrozenIndexes) ?? 0;
  let expectedIndex = getRowPositionFromClientPos(
    window.y,
    window.height,
    sheet,
    rowViewport,
    freezedRowsSum,
    clientY,
    true
  );

  if (expectedIndex === end) {
    const secondToLastElement = _.nth(rowViewport.nonFrozenIndexes, -2);
    if (secondToLastElement !== undefined) {
      expectedIndex = secondToLastElement;
    }
  }

  return expectedIndex;
};
// #endregion
